import React, { useEffect, useState, useCallback } from 'react';
import { CreditCard, Plus, Edit2, Check } from 'lucide-react';
import apiClient from '../../utils/api';
import { useToast } from '../../context/ToastContext';

const PLAN_COLORS: Record<string, string> = { FREE: '#94a3b8', STARTER: '#3b82f6', PRO: '#6366f1', ENTERPRISE: '#f59e0b' };

export const SuperAdminBilling: React.FC = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [assignModal, setAssignModal] = useState<any>(null);
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [planForm, setPlanForm] = useState({ name: '', code: '', monthlyPrice: '', currency: 'RWF', maxShipmentsPerMonth: '', maxUsers: '', maxBranches: '', maxApiKeys: '', allowWebhooks: false, allowApiIntegration: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, overviewRes] = await Promise.all([apiClient.getBillingPlans(), apiClient.getBillingOverview()]);
      setPlans(plansRes.data.data ?? []);
      const d = overviewRes.data.data;
      setCompanies(Array.isArray(d) ? d : []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (plan: any) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name, code: plan.code, monthlyPrice: String(plan.monthlyPrice),
      currency: plan.currency, maxShipmentsPerMonth: plan.maxShipmentsPerMonth ?? '',
      maxUsers: plan.maxUsers ?? '', maxBranches: plan.maxBranches ?? '',
      maxApiKeys: plan.maxApiKeys ?? '', allowWebhooks: plan.allowWebhooks, allowApiIntegration: plan.allowApiIntegration,
    });
    setShowPlanForm(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const payload = {
        ...planForm,
        monthlyPrice: parseFloat(planForm.monthlyPrice) || 0,
        maxShipmentsPerMonth: planForm.maxShipmentsPerMonth ? parseInt(planForm.maxShipmentsPerMonth) : null,
        maxUsers: planForm.maxUsers ? parseInt(planForm.maxUsers) : null,
        maxBranches: planForm.maxBranches ? parseInt(planForm.maxBranches) : null,
        maxApiKeys: planForm.maxApiKeys ? parseInt(planForm.maxApiKeys) : null,
      };
      if (editingPlan) {
        await apiClient.updateBillingPlan(editingPlan.id, payload);
        toast('Plan updated', 'success');
      } else {
        await apiClient.createBillingPlan(payload);
        toast('Plan created', 'success');
      }
      setShowPlanForm(false); setEditingPlan(null);
      load();
    } catch { toast('Failed to save plan', 'error'); } finally { setSaving(false); }
  };

  const assignPlan = async () => {
    if (!assignPlanId) return;
    setSaving(true);
    try {
      await apiClient.assignCompanyPlan(assignModal.id, assignPlanId, assignNotes);
      toast(`Plan assigned to ${assignModal.name}`, 'success');
      setAssignModal(null); setAssignPlanId(''); setAssignNotes('');
      load();
    } catch { toast('Failed to assign plan', 'error'); } finally { setSaving(false); }
  };

  const deletePlan = async (plan: any) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.deleteBillingPlan(plan.id);
      toast('Plan deleted', 'success');
      load();
    } catch { toast('Failed to delete plan', 'error'); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CreditCard size={20} /> Billing & Plans
        </h1>
        <button className="btn btn-primary" onClick={() => { setEditingPlan(null); setPlanForm({ name: '', code: '', monthlyPrice: '', currency: 'RWF', maxShipmentsPerMonth: '', maxUsers: '', maxBranches: '', maxApiKeys: '', allowWebhooks: false, allowApiIntegration: false }); setShowPlanForm(true); }}>
          <Plus size={15} /> New Plan
        </button>
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {plans.map(plan => (
          <div key={plan.id} className="card" style={{ borderTop: `4px solid ${PLAN_COLORS[plan.code] || '#94a3b8'}`, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{plan.name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{plan.code}</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: '3px 8px' }} onClick={() => openEdit(plan)}>
                <Edit2 size={13} />
              </button>
              <button className="btn btn-ghost" style={{ padding: '3px 8px', color: '#ef4444' }} onClick={() => deletePlan(plan)} title="Delete plan">
                ×
              </button>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: PLAN_COLORS[plan.code] || '#374151', marginBottom: 12 }}>
              {parseFloat(plan.monthlyPrice).toLocaleString()} <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>{plan.currency}/mo</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>📦 {plan.maxShipmentsPerMonth ?? '∞'} shipments/mo</div>
              <div>👥 {plan.maxUsers ?? '∞'} users</div>
              <div>🏢 {plan.maxBranches ?? '∞'} branches</div>
              <div>🔑 {plan.maxApiKeys ?? '∞'} API keys</div>
              {plan.allowWebhooks && <div>✅ Webhooks</div>}
              {plan.allowApiIntegration && <div>✅ API Integration</div>}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
              {companies.filter(c => c.billingPlanId === plan.id).length} companies
            </div>
          </div>
        ))}
      </div>

      {/* Company billing table */}
      <div className="card">
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Company Billing Overview</h2>
        {loading ? <div className="loading-spinner">Loading...</div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Company</th><th>Status</th><th>Current Plan</th><th>Plan Since</th><th>Billing Notes</th><th></th></tr>
              </thead>
              <tbody>
                {companies.map((c: any) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td><span className={`badge badge-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                    <td>
                      {c.billingPlan
                        ? <span style={{ fontWeight: 600, color: PLAN_COLORS[c.billingPlan.code] || '#374151' }}>{c.billingPlan.name}</span>
                        : <span style={{ color: '#94a3b8', fontSize: 12 }}>No plan</span>}
                    </td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{c.billingPlanStartDate ? new Date(c.billingPlanStartDate).toLocaleDateString() : '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.billingNotes || '—'}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}
                        onClick={() => { setAssignModal(c); setAssignPlanId(c.billingPlanId || ''); setAssignNotes(c.billingNotes || ''); }}>
                        Assign Plan
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Plan form modal */}
      {showPlanForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{editingPlan ? 'Edit Plan' : 'New Billing Plan'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'Plan Name', key: 'name', type: 'text' },
                { label: 'Code (e.g. PRO)', key: 'code', type: 'text' },
                { label: 'Monthly Price', key: 'monthlyPrice', type: 'number' },
                { label: 'Currency', key: 'currency', type: 'text' },
                { label: 'Max Shipments/mo (blank=∞)', key: 'maxShipmentsPerMonth', type: 'number' },
                { label: 'Max Users (blank=∞)', key: 'maxUsers', type: 'number' },
                { label: 'Max Branches (blank=∞)', key: 'maxBranches', type: 'number' },
                { label: 'Max API Keys (blank=∞)', key: 'maxApiKeys', type: 'number' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{f.label}</label>
                  <input type={f.type} value={(planForm as any)[f.key]} onChange={e => setPlanForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ width: '100%' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
              {[{ label: 'Allow Webhooks', key: 'allowWebhooks' }, { label: 'Allow API Integration', key: 'allowApiIntegration' }].map(f => (
                <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={(planForm as any)[f.key]} onChange={e => setPlanForm(p => ({ ...p, [f.key]: e.target.checked }))} />
                  {f.label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { setShowPlanForm(false); setEditingPlan(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={savePlan} disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign plan modal */}
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 420 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Assign Plan — {assignModal.name}</h2>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Select Plan</label>
              <select value={assignPlanId} onChange={e => setAssignPlanId(e.target.value)} style={{ width: '100%' }}>
                <option value="">— Select a plan —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({parseFloat(p.monthlyPrice).toLocaleString()} {p.currency}/mo)</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Billing Notes (optional)</label>
              <textarea value={assignNotes} onChange={e => setAssignNotes(e.target.value)} rows={2} style={{ width: '100%', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setAssignModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={assignPlan} disabled={saving || !assignPlanId}>
                <Check size={14} /> {saving ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
